import {
    AUCTION_API_URL,
    NOTIFICATION_API_URL,
    PERFORMANCE_API_URL,
    USER_SUPPORT_API_URL,
} from 'api/APIBase';
import { getApiBaseUrl, withProjectPath } from 'services/projectContext';

// exported websocket helpers in separate file to avoid cyclic dependencies
export const SocketTypes = {
    AUCTION: 'AUCTION_SOCKET',
    NOTIFICATION: 'NOTIFICATION_SOCKET',
    PERFORMANCE: 'PERFORMANCE_SOCKET',
    USER_SUPPORT: 'USER_SUPPORT_SOCKET',
} as const;
export type SocketType = (typeof SocketTypes)[keyof typeof SocketTypes];

// API constant
export const SocketURLMap = {
    [SocketTypes.AUCTION]: AUCTION_API_URL,
    [SocketTypes.NOTIFICATION]: NOTIFICATION_API_URL,
    [SocketTypes.PERFORMANCE]: `${PERFORMANCE_API_URL}/referrals`,
    [SocketTypes.USER_SUPPORT]: `${USER_SUPPORT_API_URL}/conversations`,
} as const;

const wsHttpBase = (): string => {
    const wsBase = process.env.REACT_APP_WS_BASE_URL?.trim();
    if (wsBase) {
        return wsBase;
    }
    return process.env.REACT_APP_BASE_URL;
};

export const getSocketURL = (socketType: SocketType) => {
    const path = SocketURLMap[socketType];
    const projectPath = withProjectPath(path);
    // A project environment's ingress claims /proj/<name>/… on the shared API
    // host only, so a project-scoped socket must use that host rather than the
    // dedicated websocket host. Without a project, behaviour is unchanged.
    const base = projectPath === path ? wsHttpBase() : getApiBaseUrl();
    return `${base.replace('http', 'ws')}${projectPath}`;
};

export const wsConnect = ({ socketType, npi }: { socketType: SocketType; npi: string }) => ({
    type: 'WS_CONNECT',
    socketType,
    npi,
});
export const wsEnsureConnected = ({
    socketType,
    npi,
}: {
    socketType: SocketType;
    npi: string;
}) => ({
    type: 'WS_ENSURE_CONNECTED',
    socketType,
    npi,
});
export const wsDisconnect = ({ socketType }: { socketType: SocketType }) => ({
    type: 'WS_DISCONNECT',
    socketType,
});
export const wsSend = ({
    socketType,
    messageType,
    message,
}: {
    socketType: SocketType;
    messageType: string;
    message: unknown;
}) => ({
    type: 'WS_SEND',
    socketType,
    messageType,
    message,
});
